import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';


const ListCleanerVerifier = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    selectedList: '',
    removeDuplicates: true,
    removeRoleBased: true,
    removeToxic: true,
    syntaxValidation: true,
    liveVerification: true,
    confidenceThreshold: 85
  });
  const [verificationJob, setVerificationJob] = useState(null);
  const [progress, setProgress] = useState(0);

  const contactLists = [
    { value: 'list-1', label: 'Premium Customers - Platinum Tier (8,920 contacts)' },
    { value: 'list-2', label: 'Active Subscribers - Gold Tier (15,420 contacts)' },
    { value: 'list-3', label: 'Regular Subscribers - Silver Tier (12,340 contacts)' },
    { value: 'list-4', label: 'Inactive Users - Bronze Tier (6,780 contacts)' },
    { value: 'list-5', label: 'New Leads - Lead Tier (23,450 contacts)' }
  ];

  const handleStartVerification = () => {
    setCurrentStep(4);
    setProgress(0);
    setVerificationJob({
      id: 'job-' + Date.now(),
      status: 'running',
      totalToVerify: 12340,
      verifiedCount: 0,
      startedAt: new Date()?.toISOString()
    });

    // Simulate verification progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setCurrentStep(5);
          setVerificationJob({
            ...verificationJob,
            status: 'completed',
            verifiedCount: 12340,
            completedAt: new Date()?.toISOString(),
            results: {
              deliverableHighConf: 9876,
              deliverableLowConf: 1234,
              undeliverable: 890,
              unknownGreylisted: 234,
              invalidSyntax: 106
            }
          });
          return 100;
        }
        return prev + 2;
      });
    }, 100);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {[1, 2, 3, 4, 5]?.map((step) => (
        <div key={step} className="flex items-center flex-1">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
            currentStep >= step
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-background text-muted-foreground'
          }`}>
            {currentStep > step ? (
              <Icon name="Check" size={20} />
            ) : (
              <span className="text-sm font-medium">{step}</span>
            )}
          </div>
          {step < 5 && (
            <div className={`flex-1 h-0.5 mx-2 ${
              currentStep > step ? 'bg-primary' : 'bg-border'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
          Step 1: Select Contact List
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Choose the contact list you want to clean and verify
        </p>
      </div>

      <Select
        label="Contact List"
        placeholder="Select a list"
        options={contactLists}
        value={formData?.selectedList}
        onChange={(value) => setFormData({ ...formData, selectedList: value })}
      />

      <div className="flex justify-end">
        <Button
          onClick={() => setCurrentStep(2)}
          disabled={!formData?.selectedList}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Next: Choose Options
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
          Step 2: Choose Cleaning Options
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Select which cleaning and verification operations to perform
        </p>
      </div>

      <div className="bg-muted rounded-lg p-6 space-y-4">
        <Checkbox
          label="Remove Duplicates"
          description="Remove duplicate email addresses from the list"
          checked={formData?.removeDuplicates}
          onChange={(e) => setFormData({ ...formData, removeDuplicates: e?.target?.checked })}
        />
        <Checkbox
          label="Remove Role-Based Emails"
          description="Remove generic addresses like info@, support@, admin@"
          checked={formData?.removeRoleBased}
          onChange={(e) => setFormData({ ...formData, removeRoleBased: e?.target?.checked })}
        />
        <Checkbox
          label="Remove Toxic Domains"
          description="Remove known spam traps and disposable email domains"
          checked={formData?.removeToxic}
          onChange={(e) => setFormData({ ...formData, removeToxic: e?.target?.checked })}
        />
        <Checkbox
          label="Syntax Validation"
          description="Validate email format and structure"
          checked={formData?.syntaxValidation}
          onChange={(e) => setFormData({ ...formData, syntaxValidation: e?.target?.checked })}
        />
        <div className="border-t border-border pt-4">
          <Checkbox
            label="Run Live Email Verification"
            description="Perform SMTP verification to check if emails are deliverable (recommended)"
            checked={formData?.liveVerification}
            onChange={(e) => setFormData({ ...formData, liveVerification: e?.target?.checked })}
          />
        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setCurrentStep(1)} iconName="ArrowLeft">
          Back
        </Button>
        <Button onClick={() => setCurrentStep(3)} iconName="ArrowRight" iconPosition="right">
          Next: Set Threshold
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
          Step 3: Set Confidence Threshold
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Set the minimum confidence score required to keep an email address
        </p>
      </div>

      <div className="bg-muted rounded-lg p-6">
        <div className="mb-6">
          <label className="text-sm font-medium text-foreground mb-4 block">
            Minimum Confidence Score: {formData?.confidenceThreshold}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={formData?.confidenceThreshold}
            onChange={(e) => setFormData({ ...formData, confidenceThreshold: parseInt(e?.target?.value) })}
            className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-2">
            <span>0% (Keep All)</span>
            <span>50% (Moderate)</span>
            <span>100% (Strict)</span>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="mb-2">
                <strong className="text-foreground">Recommended: 85%</strong>
              </p>
              <p>
                Emails with confidence scores below this threshold will be marked as "Bounced" and excluded from future campaigns.
                Higher thresholds provide better list quality but may remove valid addresses.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setCurrentStep(2)} iconName="ArrowLeft">
          Back
        </Button>
        <Button onClick={handleStartVerification} iconName="Play" iconPosition="right">
          Start Verification
        </Button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
          Step 4: Verification in Progress
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Please wait while we verify your contact list
        </p>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Overall Progress</span>
            <span className="text-sm font-medium text-primary">{progress}%</span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="FileText" size={16} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Stage</span>
            </div>
            <div className="text-sm font-medium text-foreground">
              {progress < 25 ? 'Syntax Validation' :
               progress < 50 ? 'DNS Lookup' :
               progress < 75 ? 'SMTP Verification': 'ML Scoring'}
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Users" size={16} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Total</span>
            </div>
            <div className="text-sm font-medium text-foreground">
              {verificationJob?.totalToVerify?.toLocaleString()}
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-xs text-muted-foreground">Verified</span>
            </div>
            <div className="text-sm font-medium text-foreground">
              {Math.round((verificationJob?.totalToVerify * progress) / 100)?.toLocaleString()}
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Remaining</span>
            </div>
            <div className="text-sm font-medium text-foreground">
              {Math.round((verificationJob?.totalToVerify * (100 - progress)) / 100)?.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
          Step 5: Verification Complete
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Your contact list has been successfully verified
        </p>
      </div>

      <div className="bg-success/10 rounded-lg border border-success/20 p-6 mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Icon name="CheckCircle" size={24} className="text-success" />
          <span className="text-lg font-semibold text-success">Verification Complete</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Processed {verificationJob?.verifiedCount?.toLocaleString()} contacts in {Math.round((new Date(verificationJob?.completedAt) - new Date(verificationJob?.startedAt)) / 1000)}s
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Deliverable (High Confidence)</span>
            <button className="text-primary hover:text-primary/80">
              <Icon name="HelpCircle" size={16} />
            </button>
          </div>
          <div className="text-3xl font-heading font-bold text-success mb-1">
            {verificationJob?.results?.deliverableHighConf?.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground">
            {((verificationJob?.results?.deliverableHighConf / verificationJob?.verifiedCount) * 100)?.toFixed(1)}% of total
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Deliverable (Low Confidence)</span>
            <button className="text-primary hover:text-primary/80">
              <Icon name="HelpCircle" size={16} />
            </button>
          </div>
          <div className="text-3xl font-heading font-bold text-warning mb-1">
            {verificationJob?.results?.deliverableLowConf?.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground">
            {((verificationJob?.results?.deliverableLowConf / verificationJob?.verifiedCount) * 100)?.toFixed(1)}% of total
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Undeliverable</span>
            <button className="text-primary hover:text-primary/80">
              <Icon name="HelpCircle" size={16} />
            </button>
          </div>
          <div className="text-3xl font-heading font-bold text-error mb-1">
            {verificationJob?.results?.undeliverable?.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground">
            {((verificationJob?.results?.undeliverable / verificationJob?.verifiedCount) * 100)?.toFixed(1)}% of total
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Unknown (Greylisted)</span>
            <button className="text-primary hover:text-primary/80">
              <Icon name="HelpCircle" size={16} />
            </button>
          </div>
          <div className="text-3xl font-heading font-bold text-muted-foreground mb-1">
            {verificationJob?.results?.unknownGreylisted?.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground">
            {((verificationJob?.results?.unknownGreylisted / verificationJob?.verifiedCount) * 100)?.toFixed(1)}% of total
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Invalid Syntax</span>
            <button className="text-primary hover:text-primary/80">
              <Icon name="HelpCircle" size={16} />
            </button>
          </div>
          <div className="text-3xl font-heading font-bold text-error mb-1">
            {verificationJob?.results?.invalidSyntax?.toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground">
            {((verificationJob?.results?.invalidSyntax / verificationJob?.verifiedCount) * 100)?.toFixed(1)}% of total
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" iconName="Download">
          Export Results
        </Button>
        <Button onClick={() => { setCurrentStep(1); setVerificationJob(null); }} iconName="RefreshCw">
          Verify Another List
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {renderStepIndicator()}
      
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
      {currentStep === 4 && renderStep4()}
      {currentStep === 5 && renderStep5()}
    </div>
  );
};

export default ListCleanerVerifier;